// frontend/src/components/jobPlacement/JobSearchPage.jsx
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";
import JobSearchContactForm from "./JobSearchContactForm";

const JobSearchPage = () => {
  const { user } = useContext(AuthContext);
  const apiBase = getApiBaseURL();
  const navigate = useNavigate();

  const [consumers, setConsumers] = useState([]);
  const [selectedConsumer, setSelectedConsumer] = useState("all");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // Date range for filtering/PDF generation
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Employer filter
  const [selectedEmployer, setSelectedEmployer] = useState("all");
  const [uniqueEmployers, setUniqueEmployers] = useState([]);

  // View states
  const [view, setView] = useState("list");
  const [selectedContact, setSelectedContact] = useState(null);
  const [consumerForForm, setConsumerForForm] = useState(null);

  // Load consumers assigned to user
  useEffect(() => {
    const loadConsumers = async () => {
      if (!user?.tenant_id) return;

      setLoading(true);
      try {
        const res = await secureAxios.get(`${apiBase}/api/consumers`, {
          params: { tenant_id: user.tenant_id },
        });
        setConsumers(res.data);
      } catch (err) {
        console.error("Error loading consumers:", err);
        toast.error("Failed to load consumers");
      } finally {
        setLoading(false);
      }
    };
    loadConsumers();
  }, [user?.tenant_id, apiBase]);

  // Load contacts when consumer selection or date range changes
  const loadContacts = async () => {
    if (!user?.tenant_id) return;

    setContactsLoading(true);
    try {
      const params = {
        tenant_id: user.tenant_id,
      };

      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      let res;
      if (selectedConsumer === "all") {
        res = await secureAxios.get(`${apiBase}/api/job-search-contacts`, {
          params,
        });
      } else {
        res = await secureAxios.get(
          `${apiBase}/api/job-search-contacts/consumer/${selectedConsumer}`,
          { params }
        );
      }

      const allContacts = res.data;

      // Extract unique employer names for the filter dropdown
      const employers = [
        ...new Set(
          allContacts
            .map((c) => c.employer_name)
            .filter((name) => name && name.trim())
        ),
      ].sort((a, b) => a.localeCompare(b));
      setUniqueEmployers(employers);

      setContacts(allContacts);
    } catch (err) {
      console.error("Error loading contacts:", err);
      toast.error("Failed to load job search contacts");
    } finally {
      setContactsLoading(false);
    }
  };

  // Filter contacts by employer (client-side filtering)
  const filteredContacts =
    selectedEmployer === "all"
      ? contacts
      : contacts.filter((c) => c.employer_name === selectedEmployer);

  useEffect(() => {
    if (user?.tenant_id) {
      loadContacts();
    }
  }, [selectedConsumer, startDate, endDate, user?.tenant_id]);

  const handleGeneratePDF = async () => {
    setGeneratingPDF(true);
    try {
      const params = {
        tenant_id: user.tenant_id,
      };

      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedEmployer !== "all") params.employer_name = selectedEmployer;

      let res;
      if (selectedConsumer === "all") {
        res = await secureAxios.get(
          `${apiBase}/api/job-search-contacts/crp33-all`,
          { params }
        );
      } else {
        res = await secureAxios.get(
          `${apiBase}/api/job-search-contacts/crp33/${selectedConsumer}`,
          { params }
        );
      }

      if (res.data?.pdf_url) {
        window.open(res.data.pdf_url, "_blank");
        if (selectedConsumer === "all") {
          toast.success(
            `CRP-33 generated with ${res.data.contact_count} contacts for ${res.data.consumer_count} consumers`
          );
        } else {
          toast.success(
            `CRP-33 generated with ${res.data.contact_count} contacts`
          );
        }
      }
    } catch (err) {
      console.error("Error generating PDF:", err);
      toast.error("Failed to generate CRP-33 PDF");
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleDeleteContact = async (contact) => {
    if (
      !window.confirm(
        `Delete contact with ${contact.employer_name} on ${formatDate(
          contact.contact_date
        )}?`
      )
    ) {
      return;
    }

    try {
      await secureAxios.delete(
        `${apiBase}/api/job-search-contacts/${contact.contact_id}`,
        { params: { tenant_id: user.tenant_id } }
      );
      toast.success("Contact deleted");
      loadContacts();
    } catch (err) {
      console.error("Error deleting contact:", err);
      toast.error("Failed to delete contact");
    }
  };

  const handleAddContact = () => {
    if (selectedConsumer === "all") {
      setConsumerForForm(null);
    } else {
      const consumer = consumers.find(
        (c) => c.consumer_id === selectedConsumer
      );
      setConsumerForForm(consumer);
    }
    setSelectedContact(null);
    setView("form");
  };

  const handleEditContact = (contact) => {
    const consumer = consumers.find(
      (c) => c.consumer_id === contact.consumer_id
    );
    setConsumerForForm(consumer);
    setSelectedContact(contact);
    setView("form");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  const getConsumerName = (consumerId) => {
    const consumer = consumers.find((c) => c.consumer_id === consumerId);
    return consumer?.name || "Unknown";
  };

  // Render contact form
  if (view === "form") {
    return (
      <div className="container-fluid px-2 px-md-4 mt-3 mt-md-4">
        <JobSearchContactForm
          contact={selectedContact}
          consumer={consumerForForm}
          consumers={consumers}
          jobPlacement={null}
          onCancel={() => {
            setView("list");
            setSelectedContact(null);
            setConsumerForForm(null);
          }}
          onSave={() => {
            setView("list");
            setSelectedContact(null);
            setConsumerForForm(null);
            loadContacts();
          }}
        />
      </div>
    );
  }

  return (
    <div className="container my-4">
    <div className="container-fluid px-2 px-md-4 mt-3 mt-md-4">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-2 mb-md-3 gap-2">
        <h4 className="mb-0 h5">
          <i className="bi bi-search me-2"></i>
          <span className="d-none d-sm-inline">CRP-33 </span>Job Search Data Sheet
        </h4>
        <span className="badge bg-warning text-dark">
          <i className="bi bi-shield-lock me-1"></i>
          HIPAA Protected
        </span>
      </div>

      {/* Info Alert */}
      <div className="alert alert-info mb-2 mb-md-3 py-2 small">
        <i className="bi bi-info-circle me-2"></i>
        Track employer contacts during the job search process.
        <span className="d-none d-md-inline"> View all contacts or filter by consumer, then generate the CRP-33 PDF report for a specific consumer.</span>
      </div>

      {/* Filters */}
      <div className="card mb-2 mb-md-3">
        <div className="card-header bg-primary text-white py-2">
          <i className="bi bi-funnel me-2"></i>
          Filters
        </div>
        <div className="card-body p-2 p-md-3">
          <div className="row align-items-end g-2">
            <div className="col-6 col-md-3">
              <label className="form-label small mb-1">Consumer</label>
              {loading ? (
                <div className="text-muted small">Loading...</div>
              ) : (
                <select
                  className="form-select form-select-sm"
                  value={selectedConsumer}
                  onChange={(e) => setSelectedConsumer(e.target.value)}
                >
                  <option value="all">All Consumers</option>
                  {consumers.map((c) => (
                    <option key={c.consumer_id} value={c.consumer_id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small mb-1">Employer</label>
              <select
                className="form-select form-select-sm"
                value={selectedEmployer}
                onChange={(e) => setSelectedEmployer(e.target.value)}
              >
                <option value="all">All Employers</option>
                {uniqueEmployers.map((employer) => (
                  <option key={employer} value={employer}>
                    {employer}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label small mb-1">Start Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label small mb-1">End Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-2">
              {(startDate ||
                endDate ||
                selectedConsumer !== "all" ||
                selectedEmployer !== "all") && (
                <button
                  className="btn btn-outline-secondary btn-sm w-100"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    setSelectedConsumer("all");
                    setSelectedEmployer("all");
                  }}
                >
                  <i className="bi bi-x me-1"></i>
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-2 mb-md-3 gap-2">
        <div className="small">
          <strong>
            {selectedConsumer === "all"
              ? "All Consumers"
              : consumers.find((c) => c.consumer_id === selectedConsumer)
                  ?.name || "Selected Consumer"}
          </strong>
          {selectedEmployer !== "all" && (
            <span className="badge bg-info ms-2">{selectedEmployer}</span>
          )}
          <span className="text-muted ms-2">
            ({filteredContacts.length} contact
            {filteredContacts.length !== 1 ? "s" : ""})
          </span>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <button
            className="btn btn-success btn-sm"
            onClick={handleGeneratePDF}
            disabled={generatingPDF || filteredContacts.length === 0}
            title={
              filteredContacts.length === 0
                ? "No contacts to include in PDF"
                : "Generate CRP-33 PDF"
            }
          >
            {generatingPDF ? (
              <>
                <span className="spinner-border spinner-border-sm me-1"></span>
                <span className="d-none d-sm-inline">Generating...</span>
              </>
            ) : (
              <>
                <i className="bi bi-file-pdf me-1"></i>
                <span className="d-none d-sm-inline">Generate </span>CRP-33
                {selectedConsumer === "all" && <span className="d-none d-md-inline"> (All)</span>}
              </>
            )}
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => navigate("/crm/placement-outreach?action=new")}
          >
            <i className="bi bi-plus-circle me-1"></i>
            <span className="d-none d-sm-inline">Log </span>Outreach
          </button>
        </div>
      </div>

      {/* Contacts Table/Cards */}
      <div className="card">
        <div className="card-header bg-secondary text-white py-2">
          <i className="bi bi-telephone me-2"></i>
          Job Search Contacts
        </div>
        <div className="card-body p-2 p-md-3">
          {contactsLoading ? (
            <div className="text-center py-3 py-md-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="text-center py-3 py-md-5">
              <i
                className="bi bi-telephone text-muted"
                style={{ fontSize: "3rem" }}
              ></i>
              <h5 className="mt-3 text-muted">No Job Search Contacts</h5>
              <p className="text-muted small">
                {startDate || endDate || selectedEmployer !== "all"
                  ? "No contacts found matching the selected filters"
                  : "Start logging employer contacts to track job search progress"}
              </p>
              <button className="btn btn-primary btn-sm" onClick={handleAddContact}>
                <i className="bi bi-plus-circle me-2"></i>
                Log First Contact
              </button>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="table-responsive d-none d-lg-block">
                <table className="table table-striped table-hover table-sm">
                  <thead className="table-dark">
                    <tr>
                      <th style={{ width: "100px" }}>Date</th>
                      {selectedConsumer === "all" && <th>Consumer</th>}
                      <th>Employer</th>
                      <th>Result of Contact</th>
                      <th>Comments</th>
                      <th style={{ width: "100px" }} className="text-end">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContacts.map((contact) => (
                      <tr key={contact.contact_id}>
                        <td>{formatDate(contact.contact_date)}</td>
                        {selectedConsumer === "all" && (
                          <td>
                            <span
                              className="text-primary"
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                setSelectedConsumer(contact.consumer_id)
                              }
                              title="Click to filter by this consumer"
                            >
                              {contact.consumer_name ||
                                getConsumerName(contact.consumer_id)}
                            </span>
                          </td>
                        )}
                        <td>
                          <span
                            className="text-primary"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              setSelectedEmployer(contact.employer_name)
                            }
                            title="Click to filter by this employer"
                          >
                            <strong>{contact.employer_name}</strong>
                          </span>
                        </td>
                        <td>{contact.result_of_contact || "-"}</td>
                        <td>
                          {contact.comments ? (
                            <span title={contact.comments}>
                              {contact.comments.length > 50
                                ? contact.comments.substring(0, 50) + "..."
                                : contact.comments}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="text-end">
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => handleEditContact(contact)}
                              title="Edit"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => handleDeleteContact(contact)}
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="d-block d-lg-none">
                {filteredContacts.map((contact) => (
                  <div key={contact.contact_id} className="card mb-2">
                    <div className="card-body p-2">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <div 
                            className="fw-bold text-primary text-truncate"
                            style={{ cursor: "pointer" }}
                            onClick={() => setSelectedEmployer(contact.employer_name)}
                          >
                            {contact.employer_name}
                          </div>
                          {selectedConsumer === "all" && (
                            <div 
                              className="small text-muted text-truncate"
                              style={{ cursor: "pointer" }}
                              onClick={() => setSelectedConsumer(contact.consumer_id)}
                            >
                              <i className="bi bi-person me-1"></i>
                              {contact.consumer_name || getConsumerName(contact.consumer_id)}
                            </div>
                          )}
                        </div>
                        <small className="text-muted ms-2 text-nowrap">
                          {formatDate(contact.contact_date)}
                        </small>
                      </div>
                      
                      {contact.result_of_contact && (
                        <div className="small mb-1">
                          <span className="badge bg-secondary me-1">Result</span>
                          {contact.result_of_contact}
                        </div>
                      )}
                      
                      {contact.comments && (
                        <div 
                          className="small text-muted mb-2"
                          style={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {contact.comments}
                        </div>
                      )}
                      
                      <div className="d-flex justify-content-end">
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handleEditContact(contact)}
                          >
                            <i className="bi bi-pencil me-1"></i>
                            Edit
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDeleteContact(contact)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Security Footer - Hidden on mobile */}
      <div className="mt-3 mt-md-4 p-2 p-md-3 bg-light rounded d-none d-md-block">
        <h6 className="mb-2">
          <i className="bi bi-shield-check me-2"></i>
          Security Information
        </h6>
        <small className="text-muted">
          <ul className="mb-0">
            <li>All data is encrypted using AES-256 encryption</li>
            <li>Access logs are maintained for audit purposes</li>
            <li>Reports are stored in HIPAA-compliant cloud storage</li>
          </ul>
        </small>
      </div>
    </div>
    </div>
  );
};

export default JobSearchPage;